<template>
  <div class="node-list-wrap">
    <div v-if="keyword" class="sitemap-search-results">
      <button
        v-for="node in searchResults"
        :key="node._key"
        type="button"
        @click="$emit('selectSearchResult', node)"
      >
        <strong>{{ node._sitemapDisplayName || node.pathname }}</strong>
        <span>{{ node._sitemapDisplayDomain || node.domain }} · {{ node.title || node.name }}</span>
      </button>
      <p v-if="searchResults.length === 0">{{ $t('observedSitemap.noResults') }}</p>
    </div>

    <div
      v-else
      class="node-list sitemap-node-list"
      :style="{ width: listWidth + 'px', height: listHeight + 'px' }"
    >
      <svg
        class="sitemap-paths"
        :width="listWidth"
        :height="listHeight"
      >
        <defs>
          <linearGradient
            v-for="path in visiblePaths"
            :key="`${path.key}-gradient-color`"
            :id="getArrowGradientColorId(path.source, path.child)"
            gradientUnits="userSpaceOnUse"
            :x1="getArrowGradientPoint(path.source, path.child, 'start').x"
            :y1="getArrowGradientPoint(path.source, path.child, 'start').y"
            :x2="getArrowGradientPoint(path.source, path.child, 'end').x"
            :y2="getArrowGradientPoint(path.source, path.child, 'end').y"
          >
            <stop offset="0" :stop-color="getArrowStartColor(path.source)"></stop>
            <stop
              :offset="getArrowGradientOffset(path.source, path.child)"
              :stop-color="getArrowEndColor(path.source)"
            ></stop>
          </linearGradient>
          <linearGradient
            v-for="path in visiblePaths"
            :key="`${path.key}-gradient`"
            :id="getArrowGradientId(path.source, path.child)"
            gradientUnits="userSpaceOnUse"
            :x1="getArrowGradientPoint(path.source, path.child, 'start').x"
            :y1="getArrowGradientPoint(path.source, path.child, 'start').y"
            :x2="getArrowGradientPoint(path.source, path.child, 'end').x"
            :y2="getArrowGradientPoint(path.source, path.child, 'end').y"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            :xlink:href="`#${getArrowGradientColorId(path.source, path.child)}`"
          ></linearGradient>
          <marker
            v-for="path in visiblePaths"
            :key="`${path.key}-head`"
            :id="getArrowHeadId(path.source, path.child)"
            orient="auto"
            refX="0.05"
            :refY="markerWidth / 2"
          >
            <path
              :d="`M0,0 V${markerWidth} L${markerHeight},${markerWidth / 2} Z`"
              :fill="getArrowEndColor(path.source)"
              stroke="none"
            ></path>
          </marker>
        </defs>
        <transition-group
          name="sitemap-path"
          tag="g"
        >
          <path
            v-for="path in visiblePaths"
            :key="path.key"
            :d="getPath(path.source, path.child)"
            :marker-end="`url(#${getArrowHeadId(path.source, path.child)})`"
            :stroke="`url(#${getArrowGradientId(path.source, path.child)})`"
          />
        </transition-group>
      </svg>

      <transition-group
        name="slide-fade"
        tag="div"
        class="sitemap-node-layer"
      >
        <ba-sitemap-node-item
          v-for="node in visibleNodes"
          :key="node._key"
          :node="node"
          :layout="getLayout(node)"
          :node-width="nodeWidth"
          :height="getNodeHeight(node)"
          :active="selectedKey === node._key"
          :expanded="!!expandedMap[node._key]"
          :compact="thumbnailHidden || isVirtualGroupNode(node)"
          :default-thumbnail="defaultThumbnail"
          :virtual-group="isVirtualGroupNode(node)"
          @select="$emit('select', $event)"
          @toggle="$emit('toggle', $event)"
        />
      </transition-group>

      <button
        v-if="hasMoreRootChildren"
        type="button"
        class="more-node depth-one"
        :style="{
          left: moreButtonLayout.left + 'px',
          top: moreButtonLayout.top + 'px'
        }"
        @click="showMoreRootChildren"
      >
        <i class="icon icon--more-x"></i>
      </button>

      <button
        v-for="button in childMoreButtons"
        :key="button.key"
        type="button"
        class="more-node child"
        :style="{
          left: button.left + 'px',
          top: button.top + 'px'
        }"
        @click="showMoreChildren(button.node)"
      >
        <i class="icon icon--more-y"></i>
      </button>
    </div>
  </div>
</template>

<script>
import {
  shouldShowNodeInfo
} from '@/libs/sitemap'
import BaSitemapNodeItem from '@/components/sitemap/NodeItem'

export default {
  name: 'BaSitemapNodeList',
  components: {
    BaSitemapNodeItem
  },
  props: {
    root: {
      type: Object,
      required: true
    },
    keyword: {
      type: String,
      default: ''
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    expandedMap: {
      type: Object,
      required: true
    },
    selectedKey: {
      type: String,
      default: null
    },
    paneWrapSize: {
      type: Array,
      default: () => [0, 0]
    },
    defaultThumbnail: {
      type: String,
      default: ''
    },
    thumbnailHidden: {
      type: Boolean,
      default: false
    },
    reportShowing: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      nodeWidth: 176,
      expandedNodeHeight: 129,
      compactNodeHeight: 36,
      inspectionInfoHeight: 52,
      columnGap: 30,
      expandedRowGap: 34,
      compactRowGap: 34,
      nodeBaseTop: 0,
      depthGap: 200,
      childIndent: 56,
      arrowEndGap: 8,
      canvasPaddingX: 36,
      canvasPaddingBottom: 80,
      rootChildrenStep: 10,
      visibleRootChildrenCount: 10,
      rootChildrenCountCustomized: false,
      childInitialLimit: 10,
      childStep: 10,
      moreButtonHeight: 41,
      childMoreButtonHeight: 37,
      moreButtonGap: 18,
      arrowWeight: 4,
      visibleChildrenCountMap: {}
    }
  },
  watch: {
    root () {
      this.rootChildrenCountCustomized = false
      this.visibleRootChildrenCount = this.defaultRootChildrenCount
      this.visibleChildrenCountMap = {}
    },
    defaultRootChildrenCount: {
      immediate: true,
      handler (value) {
        if (!this.rootChildrenCountCustomized) {
          this.visibleRootChildrenCount = value
        }
      }
    }
  },
  computed: {
    markerWidth () {
      return Math.min(3, 4 / Math.log(this.arrowWeight))
    },
    markerHeight () {
      return this.markerWidth * 0.5
    },
    nodeContentHeight () {
      return this.thumbnailHidden ? this.compactNodeHeight : this.expandedNodeHeight
    },
    rowGap () {
      return this.thumbnailHidden ? this.compactRowGap : this.expandedRowGap
    },
    defaultRootChildrenCount () {
      const paneWidth = this.paneWrapSize[0] || document.body.clientWidth
      const branchWidth = this.nodeWidth + this.childIndent * 2
      const availableWidth = Math.max(branchWidth, paneWidth - 160)
      const count = Math.floor((availableWidth + this.columnGap) / (branchWidth + this.columnGap))
      return Math.max(1, Math.min(10, count))
    },
    rootChildren () {
      return this.expandedMap[this.root._key] ? this.root.items : []
    },
    visibleRootChildren () {
      return this.rootChildren.slice(0, this.visibleRootChildrenCount)
    },
    hasMoreRootChildren () {
      return this.visibleRootChildrenCount < this.rootChildren.length
    },
    visibleNodes () {
      return this.layoutState.visibleNodes
    },
    layoutMap () {
      return this.layoutState.layoutMap
    },
    branchLayouts () {
      return this.layoutState.branchLayouts
    },
    visiblePathNodes () {
      return this.layoutState.visiblePathNodes
    },
    visiblePaths () {
      return this.layoutState.visiblePaths
    },
    listWidth () {
      return this.layoutBounds.width
    },
    listHeight () {
      return this.layoutBounds.height
    },
    layoutBounds () {
      return this.layoutState.layoutBounds
    },
    moreButtonLayout () {
      return this.layoutState.moreButtonLayout
    },
    childMoreButtons () {
      return this.layoutState.childMoreButtons
    },
    layoutState () {
      const layoutMap = {}
      const nodeHeightMap = {}
      const visibleChildrenMap = {}
      const visibleNodes = [this.root]
      const childMoreButtons = []
      const visiblePathNodes = []

      const getNodeHeight = node => {
        if (nodeHeightMap[node._key] == null) {
          nodeHeightMap[node._key] = this.computeNodeHeight(node)
        }
        return nodeHeightMap[node._key]
      }
      const getVisibleChildren = node => {
        if (visibleChildrenMap[node._key]) return visibleChildrenMap[node._key]
        if (!this.expandedMap[node._key]) return []
        const visibleChildren = node._key === this.root._key
          ? this.visibleRootChildren
          : node.items.slice(0, this.getVisibleChildrenCount(node))
        visibleChildrenMap[node._key] = visibleChildren
        return visibleChildren
      }
      const depthOneTop = this.nodeBaseTop + this.depthGap

      const createBranchLayout = node => {
        const nodeKeys = [node._key]
        const pathNodes = []
        const moreButtons = []

        layoutMap[node._key] = {
          node,
          left: 0,
          top: depthOneTop
        }
        visibleNodes.push(node)

        const layoutDescendants = (node, left, top, level) => {
          const nodeLayout = layoutMap[node._key]
          let subtreeBottom = nodeLayout
            ? nodeLayout.top + getNodeHeight(node)
            : top
          let maxLevel = level - 1

          if (!this.expandedMap[node._key]) {
            return {
              nextTop: top,
              subtreeBottom,
              maxLevel
            }
          }

          let nextTop = top
          const visibleChildren = getVisibleChildren(node)
          if (visibleChildren.length) {
            pathNodes.push({
              source: node,
              _key: node._key,
              visibleItems: visibleChildren
            })
          }

          visibleChildren.forEach(child => {
            layoutMap[child._key] = {
              node: child,
              left: left + (level - 1) * this.childIndent,
              top: nextTop
            }
            visibleNodes.push(child)
            nodeKeys.push(child._key)
            maxLevel = Math.max(maxLevel, level)
            nextTop += getNodeHeight(child) + this.rowGap
            const childLayout = layoutDescendants(child, left, nextTop, level + 1)
            nextTop = childLayout.nextTop
            subtreeBottom = Math.max(subtreeBottom, childLayout.subtreeBottom)
            maxLevel = Math.max(maxLevel, childLayout.maxLevel)
          })

          if (visibleChildren.length && visibleChildren.length < node.items.length) {
            const lastChild = visibleChildren[visibleChildren.length - 1]
            const lastChildLayout = layoutMap[lastChild._key]

            moreButtons.push({
              key: `more-${node._key}`,
              node,
              left: lastChildLayout
                ? lastChildLayout.left + (this.nodeWidth - this.childMoreButtonHeight) / 2
                : nodeLayout.left + this.childIndent + (this.nodeWidth - this.childMoreButtonHeight) / 2,
              top: subtreeBottom + this.moreButtonGap,
              width: this.childMoreButtonHeight,
              height: this.childMoreButtonHeight
            })
            nextTop += this.childMoreButtonHeight + this.moreButtonGap
          }

          return {
            nextTop,
            subtreeBottom,
            maxLevel
          }
        }

        const branchLayout = layoutDescendants(node, this.childIndent, depthOneTop + getNodeHeight(node) + this.rowGap, 1)

        return {
          node,
          nodeKeys,
          pathNodes,
          moreButtons,
          width: Math.max(
            this.nodeWidth + this.childIndent * 2,
            this.nodeWidth + this.childIndent * (branchLayout.maxLevel + 1)
          )
        }
      }

      let branchLeft = 0
      const branchLayouts = this.visibleRootChildren.map(node => {
        const branchLayout = createBranchLayout(node)
        const layout = {
          node,
          left: branchLeft,
          width: branchLayout.width
        }
        const absoluteLeft = this.canvasPaddingX + branchLeft

        branchLayout.nodeKeys.forEach(key => {
          layoutMap[key].left += absoluteLeft
        })
        branchLayout.moreButtons.forEach(button => {
          childMoreButtons.push({
            ...button,
            left: button.left + absoluteLeft
          })
        })
        visiblePathNodes.push(...branchLayout.pathNodes)
        branchLeft += branchLayout.width + this.columnGap

        return layout
      })

      const contentWidth = Math.max(
        this.nodeWidth,
        branchLayouts.reduce((width, branch, index) => {
          return width + branch.width + (index ? this.columnGap : 0)
        }, 0)
      )
      const rootLeft = this.canvasPaddingX + Math.max(0, (contentWidth - this.nodeWidth) / 2)

      layoutMap[this.root._key] = {
        node: this.root,
        left: rootLeft,
        top: this.nodeBaseTop
      }
      if (this.visibleRootChildren.length) {
        visiblePathNodes.unshift({
          source: this.root,
          _key: this.root._key,
          visibleItems: this.visibleRootChildren
        })
      }

      const visiblePaths = visiblePathNodes.reduce((paths, node) => {
        node.visibleItems.forEach(child => {
          paths.push({
            key: `${node._key}-${child._key}`,
            source: node.source,
            child
          })
        })
        return paths
      }, [])

      const lastBranch = branchLayouts[branchLayouts.length - 1]
      const moreButtonLeft = lastBranch
        ? this.canvasPaddingX + lastBranch.left + lastBranch.width - 40
        : this.canvasPaddingX
      const depthOneNode = (lastBranch && lastBranch.node) || this.root
      const moreButtonLayout = {
        left: moreButtonLeft,
        top: this.nodeBaseTop + this.depthGap + (getNodeHeight(depthOneNode) - this.moreButtonHeight) / 2,
        width: this.moreButtonHeight,
        height: this.moreButtonHeight
      }
      const moreButtonRight = this.hasMoreRootChildren ? moreButtonLayout.left + moreButtonLayout.width : 0
      const childMoreButtonBounds = childMoreButtons.reduce((bounds, button) => {
        return {
          right: Math.max(bounds.right, button.left + button.width),
          bottom: Math.max(bounds.bottom, button.top + button.height)
        }
      }, { right: 0, bottom: 0 })
      const nodeBounds = Object.values(layoutMap).reduce((bounds, layout) => {
        return {
          right: Math.max(bounds.right, layout.left + this.nodeWidth),
          bottom: Math.max(bounds.bottom, layout.top + getNodeHeight(layout.node))
        }
      }, { right: this.nodeWidth, bottom: getNodeHeight(this.root) })
      const maxRight = Math.max(nodeBounds.right, moreButtonRight, childMoreButtonBounds.right)
      const maxBottom = Math.max(nodeBounds.bottom, childMoreButtonBounds.bottom)
      const layoutBounds = {
        width: maxRight + this.canvasPaddingX,
        height: maxBottom + this.canvasPaddingBottom
      }

      return {
        branchLayouts,
        childMoreButtons,
        layoutBounds,
        layoutMap,
        moreButtonLayout,
        nodeHeightMap,
        visibleNodes,
        visiblePathNodes,
        visiblePaths
      }
    }
  },
  methods: {
    isVirtualGroupNode (node) {
      return Boolean(node && node._virtualGroupNode)
    },
    computeNodeHeight (node) {
      if (this.isVirtualGroupNode(node)) return this.compactNodeHeight

      return this.nodeContentHeight + (shouldShowNodeInfo(node) ? this.thumbnailHidden ? this.inspectionInfoHeight : this.inspectionInfoHeight / 2 : 0)
    },
    getNodeHeight (node) {
      return this.layoutState.nodeHeightMap[node._key] || this.computeNodeHeight(node)
    },
    getNodeConnectionBottom (node) {
      return this.getLayout(node).top + this.getNodeHeight(node) -
        (shouldShowNodeInfo(node) ? (this.thumbnailHidden ? 0 : this.inspectionInfoHeight / 2) : 2)
    },
    getArrowId (fromNode, toNode, type) {
      return `sitemap-${type}-${fromNode._key}-${toNode._key}`
    },
    getArrowGradientColorId (fromNode, toNode) {
      return this.getArrowId(fromNode, toNode, 'arrow-gradient-color')
    },
    getArrowGradientId (fromNode, toNode) {
      return this.getArrowId(fromNode, toNode, 'arrow-gradient')
    },
    getArrowHeadId (fromNode, toNode) {
      return this.getArrowId(fromNode, toNode, 'head')
    },
    isDepthOnePath (fromNode) {
      return fromNode._key === this.root._key
    },
    getArrowStartColor (fromNode) {
      if (this.reportShowing) return '#dbdbd8'
      return this.isDepthOnePath(fromNode) ? '#aecbcf' : '#CED8A0'
    },
    getArrowEndColor (fromNode) {
      if (this.reportShowing) return this.isDepthOnePath(fromNode) ? '#b0c9cd' : '#d2dea8'
      return this.isDepthOnePath(fromNode) ? '#0e90a5' : '#97bd12'
    },
    getArrowGradientPoint (fromNode, toNode, type) {
      const from = this.getLayout(fromNode)
      const to = this.getLayout(toNode)
      const isRootConnection = fromNode._key === this.root._key

      if (type === 'start') {
        return {
          x: isRootConnection ? from.left + this.nodeWidth / 2 : from.left + this.childIndent / 2,
          y: this.getNodeConnectionBottom(fromNode)
        }
      }

      if (isRootConnection) {
        return {
          x: from.left + this.nodeWidth / 2,
          y: to.top
        }
      }

      return {
        x: to.left - this.arrowEndGap,
        y: to.top + this.getNodeHeight(toNode) / 2
      }
    },
    getArrowGradientOffset (fromNode, toNode) {
      const from = this.getLayout(fromNode)
      const to = this.getLayout(toNode)
      const distance = Math.max(1, Math.hypot(to.left - from.left, to.top - from.top))
      return Math.min(70, Math.max(20, 50 / distance * 100)) + '%'
    },
    showMoreRootChildren () {
      this.rootChildrenCountCustomized = true
      this.visibleRootChildrenCount = Math.min(
        this.rootChildren.length,
        this.visibleRootChildrenCount + this.rootChildrenStep
      )
    },
    showMoreChildren (node) {
      const currentCount = this.getVisibleChildrenCount(node)
      this.$set(
        this.visibleChildrenCountMap,
        node._key,
        Math.min(node.items.length, currentCount + this.childStep)
      )
    },
    getVisibleChildrenCount (node) {
      return this.visibleChildrenCountMap[node._key] || this.childInitialLimit
    },
    getVisibleChildren (node) {
      if (!this.expandedMap[node._key]) return []
      if (node._key === this.root._key) return this.visibleRootChildren
      return node.items.slice(0, this.getVisibleChildrenCount(node))
    },
    hasMoreChildren (node) {
      return this.getVisibleChildren(node).length < node.items.length
    },
    getLayout (node) {
      return this.layoutMap[node._key] || { node, left: 0, top: 0 }
    },
    isNodeVisible (node) {
      return !!this.layoutMap[node._key]
    },
    getPath (fromNode, toNode) {
      const from = this.getLayout(fromNode)
      const to = this.getLayout(toNode)
      const fromX = from.left + this.nodeWidth / 2
      const fromY = this.getNodeConnectionBottom(fromNode)
      const toX = to.left + this.nodeWidth / 2
      const toY = to.top - 10
      const isRootConnection = fromNode._key === this.root._key

      if (isRootConnection) {
        const middleY = fromY + (toY - fromY) / 2
        const direction = toX > fromX ? 1 : -1
        const radius = Math.min(14, Math.abs(toX - fromX) / 2, Math.abs(middleY - fromY) / 2)
        return `M${fromX},${fromY} V${middleY - radius} Q${fromX},${middleY} ${fromX + direction * radius},${middleY} H${toX - direction * radius} Q${toX},${middleY} ${toX},${middleY + radius} V${toY}`
      }

      const railX = from.left + this.childIndent / 2
      const toEndX = to.left - this.arrowEndGap
      const toCenterY = to.top + this.getNodeHeight(toNode) / 2
      const secondDirection = toEndX > railX ? 1 : -1
      const secondRadius = Math.min(10, Math.abs(toEndX - railX) / 2, Math.abs(toCenterY - fromY) / 2)
      return `M${railX},${fromY} V${toCenterY - secondRadius} Q${railX},${toCenterY} ${railX + secondDirection * secondRadius},${toCenterY} H${toEndX}`
    }
  }
}
</script>

<style lang="scss" scoped>
.node-list-wrap {
  position: relative;
}

.node-list {
  position: relative;
  white-space: nowrap;
  transition: width 0.35s ease, height 0.35s ease;
}

.sitemap-node-layer {
  position: absolute;
  left: 0;
  top: 0;
}

.sitemap-paths {
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;
  pointer-events: none;

  > g path {
    fill: none;
    stroke-width: 4;
    opacity: 1;
  }

}

.sitemap-path-enter-active {
  transition: opacity 0.22s ease 0.28s;
}

.sitemap-path-leave-active {
  transition: opacity 0.12s ease;
}

.sitemap-path-enter,
.sitemap-path-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: opacity 0.5s ease, transform 0.5s ease, left 0.35s ease, top 0.35s ease;
}

.slide-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.more-node {
  position: absolute;
  width: 41px;
  height: 41px;
  padding: 8px;
  border-radius: 21px;
  background: #ffffff;
  box-shadow: 0 5px 6px -2px rgba(0, 0, 0, 0.22), 0 0 0 1px rgba(0, 0, 0, 0.15);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 5px 6px -2px rgba(0, 0, 0, 0.22), 0 0 0 2px rgba(0, 116, 255, 1);
  }

  &.child {
    width: 37px;
    height: 37px;
    padding: 6px;
    border-radius: 21px;
  }

  .icon {
    width: 100%;
    height: 100%;
    background-size: cover;
  }
}

.sitemap-search-results {
  width: 640px;
  padding-top: 95px;

  button {
    display: block;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    border-bottom: 1px solid #e5e5e1;
    background: #fff;
    text-align: left;
  }

  strong,
  span {
    display: block;
    overflow-wrap: anywhere;
  }

  strong {
    color: #252733;
    font-size: 13px;
    font-weight: 800;
  }

  span {
    margin-top: 4px;
    color: #777;
    font-size: 12px;
  }
}
</style>
